/*
 * Переписка, сохранённая на устройстве.
 *
 * До этого список чатов и сообщения жили только в памяти вкладки: каждый
 * запуск начинался со скелета и ждал сеть, а без сети показывать было нечего
 * вовсе. Здесь лежит последний известный вид приложения — тот же, что был на
 * экране, — и он рисуется мгновенно, ещё до первого ответа сервера.
 *
 * Хранится в IndexedDB, а не в localStorage: там сотни килобайт сообщений
 * писались бы строкой и на телефоне подвешивали бы отрисовку.
 *
 * Ничего критичного здесь нет: это ровно то, что человек и так видит на
 * экране. Но на общем компьютере остаться после выхода оно не должно —
 * поэтому выход стирает запись целиком.
 */

import type { Chat, ChatFolder, Message, User } from '@andresot/protocol';
import type { AccountUser } from '$api/auth';

const DB_NAME = 'andresot';
const DB_VERSION = 1;
const STORE = 'snapshot';
const KEY = 'current';

/**
 * Сколько сообщений на чат храним.
 *
 * Хватает, чтобы открытая переписка выглядела целой; остальное дочитается с
 * сервера при прокрутке. Больше — дольше запись и больше места на телефоне.
 */
const MESSAGES_PER_CHAT = 60;

/** Сколько чатов храним. Ниже по списку человек при запуске всё равно не смотрит. */
const CHATS = 200;

/** Устаревшее не показываем: за неделю от переписки могло не остаться ничего. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface Snapshot {
	/** Чьё это. Чужое не показываем — на устройстве могли войти под другим. */
	userId: string;
	savedAt: number;
	/**
	 * Кто вошёл — тот же вид, что приходит от сервера.
	 *
	 * Нужен, чтобы открыть приложение без сети: спросить сервер, кто мы, тогда
	 * не у кого, а показывать переписку без имени и аватара — полдела.
	 */
	account: AccountUser | null;
	chats: Chat[];
	users: User[];
	folders: ChatFolder[];
	activeFolderId: string;
	selectedChatId: string | null;
	/** Сообщения открытых чатов: остальные и не были загружены. */
	messages: Record<string, Message[]>;
	/** Время последней сверки по часам сервера — с него продолжится догон. */
	syncedAt: Record<string, number>;
}

function open(): Promise<IDBDatabase> {
	return new Promise((resolve, reject) => {
		const request = indexedDB.open(DB_NAME, DB_VERSION);
		request.onupgradeneeded = () => {
			if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE);
		};
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error ?? new Error('IndexedDB недоступна'));
		// Другая вкладка держит старую версию базы — ждать её нечего.
		request.onblocked = () => reject(new Error('IndexedDB занята'));
	});
}

function run<T>(store: IDBObjectStore, request: IDBRequest<T>): Promise<T> {
	return new Promise((resolve, reject) => {
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error ?? new Error('Запрос к IndexedDB не удался'));
		store.transaction.onabort = () =>
			reject(store.transaction.error ?? new Error('Запись в IndexedDB прервана'));
	});
}

/**
 * Прочитать сохранённое.
 *
 * `null` — значит показывать нечего: записи нет, она чужая, она стара или
 * хранилище недоступно (частный режим, запрет в настройках браузера). Ни один
 * из этих случаев не должен ломать запуск, поэтому все они молчаливые.
 */
export async function readSnapshot(userId?: string): Promise<Snapshot | null> {
	if (typeof indexedDB === 'undefined') return null;

	try {
		const db = await open();
		try {
			const store = db.transaction(STORE, 'readonly').objectStore(STORE);
			const saved = (await run(store, store.get(KEY))) as Snapshot | undefined;
			if (!saved) return null;
			if (userId !== undefined && saved.userId !== userId) return null;
			if (Date.now() - saved.savedAt > MAX_AGE_MS) return null;

			return saved;
		} finally {
			db.close();
		}
	} catch {
		return null;
	}
}

/** Сохранить — молча, чтобы переполненное хранилище не ломало переписку. */
export async function writeSnapshot(snapshot: Snapshot): Promise<boolean> {
	if (typeof indexedDB === 'undefined') return false;

	const trimmed: Snapshot = {
		...snapshot,
		chats: snapshot.chats.slice(0, CHATS),
		messages: Object.fromEntries(
			Object.entries(snapshot.messages).map(([chatId, messages]) => [
				chatId,
				messages.slice(-MESSAGES_PER_CHAT)
			])
		)
	};

	try {
		const db = await open();
		try {
			const store = db.transaction(STORE, 'readwrite').objectStore(STORE);
			await run(store, store.put(trimmed, KEY));
			return true;
		} finally {
			db.close();
		}
	} catch {
		// Места нет или хранилище закрыто — приложение работает и без записи.
		return false;
	}
}

/** Стереть. Вызывается при выходе: на общем компьютере чужого оставлять нельзя. */
export async function clearSnapshot(): Promise<void> {
	if (typeof indexedDB === 'undefined') return;

	try {
		const db = await open();
		try {
			const store = db.transaction(STORE, 'readwrite').objectStore(STORE);
			await run(store, store.delete(KEY));
		} finally {
			db.close();
		}
	} catch {
		// Не вышло стереть — не повод оставлять человека в приложении.
	}
}
